/**
 * MyWssSummary Component
 * Shows WSS visit summary for SH3 users with 3 tabs:
 * - Visited (green tag)
 * - Not Visited (yellow tag)
 * - New (purple tag)
 * Allows direct check-in from summary items
 */

import _ from "lodash";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Loader from "@features/shared/components/Loader";
import ModalPopup from "@components/Common/ModalPopup";
import { FaThumbsUp } from "react-icons/fa";
import { HiOutlineLocationMarker } from "react-icons/hi";
import styles from "@components/MarketWorking/MyWss/MyWssSummary.module.css";
import { getWssCheckInList } from "@services/wss/getWssCheckInList";
import { getUniqueListBy } from "@components/Common/Function";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Message from "@features/shared/components/Message";

const territoryAutoCompletePlaceHolder = {
  attributes: null,
  Id: null,
  SH2_Name__c: null,
  SH2_Code__c: null,
  Name: null,
  SH3_Code__c: null,
};

const MyWssSummary = ({ CheckIndata, reloadList }) => {
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ind] = useState();
  const [active] = useState(false);
  const tname = useSelector((state) => state?.territory?.territoryDetails);
  const divisionCode = useSelector(
    (state) => state?.userDetails?.details?.Division
  );
  const [selectedTerritory, setSelectedTerritory] = useState(
    territoryAutoCompletePlaceHolder
  );
  const [wssList, setWssList] = useState({});
  const [divisionFlag, setDivisionFlag] = useState(divisionCode === 70);
  const [wssListCount, setWssListCount] = useState({
    visited: "0",
    not_visited: "0",
    new: "0",
  });

  const tabs = [
    { key: "Visited", label: `Visited (${wssListCount.visited})` },
    { key: "Not Visited", label: `Not Visited (${wssListCount.not_visited})` },
    { key: "New", label: `New (${wssListCount.new})` },
  ];

  // API call to get Wss list against Territory
  const getList = React.useCallback(async (territoryList) => {
    setLoading(true);
    const response = await getWssCheckInList(territoryList, divisionFlag);
    if (response?.success) {
      var count = {
        visited: response?.data?.Visited?.length || 0,
        not_visited: response?.data?.NotVisited?.length || 0,
        new: response?.data?.New?.length || 0,
      };
      setWssList(response?.data);
      setWssListCount(count);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [divisionFlag]);

  // To get All territory wss list when we open the page / component
  useEffect(() => {
    if (divisionCode === 70) {
      setDivisionFlag(true);
    }
    if (!_.isEmpty(tname)) {
      getList(tname);
    }
  }, [divisionCode, tname, getList]);

  // To get territory wss list after successfull check-in Call
  useEffect(() => {
    if (!_.isEmpty(reloadList)) {
      setSelectedTerritory(reloadList);
    }
  }, [reloadList]);

  // To set territory
  useEffect(() => {
    setLoading(true);
    if (selectedTerritory?.SH2_Code__c !== null && !_.isEmpty(selectedTerritory)) {
      var list = [selectedTerritory];
      getList(list);
    } else {
      setLoading(false);
    }
  }, [selectedTerritory, getList]);

  // To achieve direct check-in functionality
  const handleAutoPopulate = (e, item) => {
    e.preventDefault();
    CheckIndata(item);
  };

  /**
   * Renders a WSS item card
   */
  const renderWssItem = (item, index, tagColor) => (
    <div
      key={index}
      className={`${styles.myWssSummary__mainBox} ${
        active && ind === index && styles.myWssSummary__mainBoxActive
      }`}
      id={index}
    >
      <div className={styles.myWssSummary__locationBox}>
        <div
          className={styles.myWssSummary__tag}
          style={{ backgroundColor: tagColor }}
        ></div>
        <div className={styles.myWssSummary__location}>
          <HiOutlineLocationMarker className={styles.myWssSummary__locationIcon} />
          <p>
            {item?.WSS_Name__c || item?.CustomerName__c} :{" "}
            {item?.WSS_Code__c || item?.Customer_Code__c}
          </p>
        </div>
      </div>
      <div className={styles.myWssSummary__dateBox}>
        <div className={styles.myWssSummary__tourDate}>
          <p className={styles.myWssSummary__tourDay}>{item.Status}</p>
        </div>
        <span className={styles.myWssSummary__divideLine}>|</span>
        <div className={styles.myWssSummary__edit}>
          <button
            className={styles.myWssSummary__downloadBtn}
            onClick={(e) => handleAutoPopulate(e, item)}
          >
            Check-In
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Loader loading={loading} />
      <div>
        {isSubmit && <Loader loading={loading} />}

        <div className={styles.myWssSummary}>
          <div className={styles.myWssSummary__heading}>My Wss Summary</div>
          <div className={styles.myWssSummary__Outermainbox}>
            <div className={styles.myWssSummary__dropdownSelection}>
              <Autocomplete
                id="grouped-demo"
                className="myDealer__categorySelect"
                options={!_.isEmpty(tname) ? getUniqueListBy(tname, "SH2_Code__c") : []}
                getOptionLabel={(option) => {
                  if (option?.SH2_Name__c) {
                    return `${option?.SH2_Name__c} : ${option?.SH2_Code__c}`;
                  } else {
                    return "";
                  }
                }}
                value={selectedTerritory}
                name="dealerId"
                onChange={(event, newValue) => {
                  setSelectedTerritory(newValue);
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select Territory" />
                )}
              />
            </div>
            <Tabs
              defaultActiveKey="Visited"
              id="fill-tab-example"
              className="newFeedback__tabHeader"
              onSelect={() => {
                setIsSubmit(false);
              }}
              fill
            >
              {tabs?.map((tabItem) => {
                return (
                  <Tab
                    eventKey={tabItem.key}
                    title={tabItem.label}
                    key={tabItem.key}
                  >
                    <div className="newFeedback__tabInnerBox">
                      <div className={styles.myWssSummary__outerInputBox}>
                        {Object.keys(wssList).length !== 0 ? (
                          <>
                            {tabItem.key === "Visited" && (
                              <>
                                {wssList.Visited?.length > 0 ? (
                                  wssList.Visited?.map((item, index) =>
                                    renderWssItem(item, index, "#A2D7AF")
                                  )
                                ) : (
                                  <Message
                                    message={"No data found"}
                                    className={"tourPlan__messageConatiner"}
                                  />
                                )}
                              </>
                            )}
                            {tabItem.key === "Not Visited" && (
                              <>
                                {wssList.NotVisited?.length > 0 ? (
                                  wssList.NotVisited?.map((item, index) =>
                                    renderWssItem(item, index, "#FDF3B5")
                                  )
                                ) : (
                                  <Message
                                    message={"No data found"}
                                    className={"tourPlan__messageConatiner"}
                                  />
                                )}
                              </>
                            )}
                            {tabItem.key === "New" && (
                              <>
                                {wssList.New?.length > 0 ? (
                                  wssList.New?.map((item, index) =>
                                    renderWssItem(item, index, "#561fff")
                                  )
                                ) : (
                                  <Message
                                    message={"No data found"}
                                    className={"tourPlan__messageConatiner"}
                                  />
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          <Message
                            message={"No data found"}
                            className={"tourPlan__messageConatiner"}
                          />
                        )}
                      </div>
                    </div>
                  </Tab>
                );
              })}
            </Tabs>
          </div>

          {isSubmitModalOpen && (
            <ModalPopup
              className="ModalPopup_Submitted"
              isModalOpen={isSubmitModalOpen}
              setIsModalOpen={setIsSubmitModalOpen}
              title="Submitted Successfully"
              data="New Observations form has been submitted successfully."
              backdrop={true}
              fade={false}
              icon={
                <>
                  <div className="submittedIcons draftIcons">
                    <FaThumbsUp className="submittedSmall" />
                    <FaThumbsUp className="submittedLarge" />
                    <FaThumbsUp className="submittedSmall" />
                  </div>
                </>
              }
            />
          )}
        </div>
      </div>
    </>
  );
};

export default MyWssSummary;
